import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, fmtUS, today, fmtTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, Badge } from "@/components/common";
import { RecordDialog } from "@/components/RecordDialog";
import { PatientTable } from "@/components/PatientTable";

const plus = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const CFG = {
  appointments: { title: "Appointments", tabs: [["upcoming", "Upcoming", { start: today() }, (r) => ["Scheduled", "Confirmed"].includes(r.status)], ["missed", "Missed Appointments", { status: "No Show" }],
    ["unconfirmed", "Status Not Confirmed", { unconfirmed: "1" }], ["completed", "Completed", { status: "Completed" }], ["all", "All", {}]] },
  prescriptions: { title: "Prescriptions", tabs: [["sent", "Prescriptions Sent", {}], ["pending", "Pending Pharmacy Confirmation", { status: "Sent" }], ["fills", "Pharmacy-Confirmed Fills", {}, null, "dispenses"]] },
  injections: { title: "Injectable PrEP", tabs: [["due", "Scheduled / Due", { status: "Scheduled" }], ["administered", "Administered", { status: "Administered" }], ["all", "All", {}]] },
  labs: { title: "Laboratory Monitoring", tabs: [["overdue", "Overdue Patients", null], ["upcoming", "Due in 30 days", null], ["records", "Laboratory Records", {}]] },
  tasks: { title: "Follow-up Tasks", tabs: [["open", "Open", { open: "1" }], ["completed", "Completed", { status: "Completed" }], ["all", "All", {}]] },
};

const COLS = {
  appointments: [["scheduled_at", "Date & Time"], ["type", "Type"], ["status", "Status", 1], ["employee_name", "Employee"], ["notes", "Notes"]],
  prescriptions: [["sent_date", "Sent Date"], ["medication", "Medication"], ["pharmacy", "Pharmacy"], ["provider", "Provider"], ["status", "Status", 1], ["fill_date", "Fill Date"], ["employee_name", "Employee"]],
  dispenses: [["fill_date", "Fill Date"], ["medication", "Medication"], ["pharmacy", "Pharmacy"], ["refill_number", "Refill #"], ["confirmation_source", "Confirmation Source"], ["employee_name", "Employee"]],
  injections: [["scheduled_date", "Scheduled"], ["administered_date", "Administered"], ["medication", "Medication"], ["status", "Status", 1], ["employee_name", "Employee"]],
  labs: [["lab_date", "Lab Date"], ["next_due", "Next Due"], ["status", "Status", 1], ["external", "External"], ["employee_name", "Employee"]],
  tasks: [["due_date", "Due Date"], ["type", "Task Type"], ["priority", "Priority", 1], ["status", "Status", 1], ["employee_name", "Assigned Employee"], ["notes", "Notes"], ["completed_at", "Completed"]],
};

function QuickStatus({ kind, r, onDone }) {
  const opts = { appointments: ["Completed", "No Show", "Cancelled", "Rescheduled", "Confirmed"], tasks: ["In Progress", "Completed"] }[kind];
  if (!opts) return null;
  const set = async (status) => {
    try {
      await api.put(`/records/${kind}/${r.id}`, { status });
      toast.success(status === "No Show" ? "Marked No Show — follow-up task and notification created" : `Marked ${status}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <select className="inp h-8 w-36 text-xs" value="" onChange={(e) => e.target.value && set(e.target.value)} onClick={(e) => e.stopPropagation()} data-testid={`quick-status-${r.id}`}>
      <option value="">Set status…</option>
      {opts.filter((o) => o !== r.status).map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

export default function RecordsPage({ kind, embedded, initialSub, onChanged }) {
  const { t, can } = useApp();
  const cfg = CFG[kind];
  const [tab, setTab] = useState(initialSub && cfg.tabs.some((x) => x[0] === initialSub) ? initialSub : cfg.tabs[0][0]);
  const cur = cfg.tabs.find((x) => x[0] === tab) || cfg.tabs[0];
  const k = cur[4] || kind;
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [n, setN] = useState(0);
  const load = useCallback(() => {
    if (!cur[2]) return;
    api.get(`/records/${k}`, { params: cur[2] }).then((r) => setRows(cur[3] ? r.data.filter(cur[3]) : r.data)).catch((e) => toast.error(errMsg(e)));
  }, [k, cur]);
  useEffect(() => { load(); }, [load, n]);
  useEffect(() => { if (n && onChanged) onChanged(); }, [n, onChanged]);
  const edit = can("action:record_edit");
  const cols = [{ key: "patient_code", label: "Patient ID", render: (r) => <Link className="font-semibold text-[#283F91] hover:underline dark:text-blue-300" to={`/patients/${r.patient_id}`}>{r.patient_code}</Link> },
    ...COLS[k].map(([c, l, b]) => ({ key: c, label: l, render: (r) => (b ? <Badge value={r[c]} /> : typeof r[c] === "boolean" ? (r[c] ? "Yes" : "No") : c.endsWith("date") || c.endsWith("_at") ? `${fmtUS(r[c])}${c === "scheduled_at" && r[c] ? " " + fmtTime(r[c].slice(11, 16)) : ""}` : r[c] ?? "—") }))];
  if (kind === "appointments") cols.push({ key: "_flag", label: "Review", sortable: false, render: (r) => (r.unconfirmed && ["Scheduled", "Confirmed"].includes(r.status) ? <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Status Not Confirmed</span> : "") });
  if (edit) cols.push({ key: "_a", label: "Actions", sortable: false, render: (r) => (
    <div className="flex gap-1.5">
      <QuickStatus kind={k} r={r} onDone={() => setN(n + 1)} />
      <button className="btn btn-ghost btn-sm" onClick={() => setDlg({ record: r })} data-testid={`edit-record-${r.id}`}>{kind === "injections" && r.status === "Scheduled" ? "Confirm administration" : "Edit"}</button>
    </div>) });
  return (
    <div>
      {embedded ? (edit && cur[2] && <div className="mb-2 flex justify-end"><button className="btn btn-primary btn-sm" onClick={() => setDlg({})} data-testid={`add-record-btn-${kind}`}><Plus size={14} />Add</button></div>)
        : <PageHeader title={t(cfg.title)} actions={edit && cur[2] && <button className="btn btn-primary" onClick={() => setDlg({})} data-testid="add-record-btn"><Plus size={15} />Add</button>} />}
      <div className="mb-4 flex flex-wrap gap-1 border-b" data-testid={`records-tabs-${kind}`}>
        {cfg.tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`records-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${tab === key ? "border-[#283F91] text-[#283F91] dark:text-blue-300" : "border-transparent text-[var(--muted-ink)] hover:text-[var(--ink)]"}`}>{t(label)}</button>
        ))}
      </div>
      {kind === "labs" && !cur[2] ? (
        <LabPatients mode={tab} />
      ) : (
        <DataTable testid={`${k}-table`} columns={cols} rows={rows} />
      )}
      <RecordDialog kind={k} record={dlg?.record} open={!!dlg} onClose={() => setDlg(null)} onSaved={() => setN(n + 1)} />
    </div>
  );
}

function LabPatients({ mode }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/patients").then((r) => setRows(r.data.filter((p) => p.status === "ACTIVE" && p.next_lab && (mode === "overdue" ? p.next_lab < today() : p.next_lab >= today() && p.next_lab <= plus(30)))));
  }, [mode]);
  if (!rows) return null;
  return (
    <DataTable testid={`labs-${mode}-table`} rows={rows} columns={[
      { key: "patient_id", label: "Patient ID", render: (r) => <Link className="font-semibold text-[#283F91] hover:underline" to={`/patients/${r.id}`}>{r.patient_id}</Link> },
      { key: "medication", label: "Medication" }, { key: "last_lab", label: "Last Labs", render: (r) => fmtUS(r.last_lab) },
      { key: "next_lab", label: "Next Labs", render: (r) => <span className={mode === "overdue" ? "font-semibold text-red-600" : ""}>{fmtUS(r.next_lab)}</span> },
      { key: "assigned_employee", label: "Assigned Employee" }, { key: "indicator", label: "Status", render: (r) => <Badge value={r.indicator} /> }]} />
  );
}

export { PatientTable };
